import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { supabase } from '../firebase/config';
import './AuthActionPage.css';

type ActionType = 'reset-password' | 'confirm-email';

const AuthActionPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const action: ActionType = location.pathname.includes('confirm-email')
    ? 'confirm-email'
    : 'reset-password';

  const token = new URLSearchParams(location.search).get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(action === 'confirm-email');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (action !== 'confirm-email') return;
    if (!token) {
      setError('This confirmation link is invalid.');
      setLoading(false);
      return;
    }

    authService
      .confirmEmailChange(token)
      .then(async () => {
        await supabase.auth.refreshSession();
        setSuccess('Your email address has been updated. You can sign in with your new email.');
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to confirm email change.');
      })
      .finally(() => setLoading(false));
  }, [action, token]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('This reset link is invalid.');
      return;
    }
    if (password.length < 6) {
      setError('Password should be at least 6 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await authService.resetPassword(token, password);
      setSuccess('Your password has been updated. You can sign in now.');
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-action-page">
      <div className="auth-action-card">
        {action === 'confirm-email' ? (
          <>
            <h1>Confirm email change</h1>
            {loading && (
              <div className="auth-action-loading">
                <div className="auth-action-spinner" />
                <p>Confirming your new email address…</p>
              </div>
            )}
            {!loading && error && <p className="auth-action-error">{error}</p>}
            {!loading && success && <p className="auth-action-success">{success}</p>}
          </>
        ) : (
          <>
            <h1>Reset your password</h1>
            <p className="auth-action-subtitle">Choose a new password for your WorldAsia account.</p>
            {error && <p className="auth-action-error">{error}</p>}
            {success ? (
              <p className="auth-action-success">{success}</p>
            ) : (
              <form className="auth-action-form" onSubmit={handleResetPassword}>
                <div className="form-group">
                  <label htmlFor="new-password">New password</label>
                  <input
                    id="new-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter a new password"
                    required
                    minLength={6}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirm-password">Confirm password</label>
                  <input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your new password"
                    required
                    minLength={6}
                    disabled={submitting}
                  />
                </div>
                <button type="submit" className="auth-action-submit" disabled={submitting || !token}>
                  {submitting ? 'Updating…' : 'Update password'}
                </button>
              </form>
            )}
          </>
        )}

        <Link to="/" className="auth-action-home-link">
          Back to home
        </Link>
      </div>
    </div>
  );
};

export default AuthActionPage;
